@import '../../utils/typography.css';
@import '../../utils/shimmer.css';

:host {
  box-sizing: border-box;
  max-width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
  font-family: var(--typography-font-family-ui);
}

:host(:where(.ds-text--inline-box)) {
  display: inline-block;
}

:host(:where(.ds-text--block-box)) {
  display: block;
  width: 100%;
}

/*
 * The host is the measurable typography box. The semantic inner element fills
 * it and inherits every text metric without adding margins, padding, or its own
 * line-height strut. One rendered line therefore measures exactly one token
 * line-height; N lines measure N × that token.
 */
.ds-text__element {
  display: block;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
  font: inherit;
  line-height: inherit;
  letter-spacing: inherit;
  color: inherit;
  text-transform: inherit;
  text-align: inherit;
  text-decoration: inherit;
  text-underline-offset: inherit;
  font-style: inherit;
  font-variant-numeric: inherit;
  font-feature-settings: inherit;
  -webkit-user-select: inherit;
  user-select: inherit;
  white-space: inherit;
  text-wrap: inherit;
  overflow-wrap: inherit;
  word-break: inherit;
  /* Consumer truncation on the host (PanelNav, ChartLegend, …) must reach the
     text node — overflow/text-overflow are not inherited by default. */
  overflow: inherit;
  text-overflow: inherit;
}

/* ── Truncation ─────────────────────────────────────────────────────────── */

:host(.ds-text--truncate-1) {
  overflow: hidden;
}

:host(.ds-text--truncate-1) .ds-text__element {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:host(.ds-text--truncate-2),
:host(.ds-text--truncate-3),
:host(.ds-text--truncate-4),
:host(.ds-text--truncate-5) {
  overflow: hidden;
}

:host(.ds-text--truncate-2) .ds-text__element,
:host(.ds-text--truncate-3) .ds-text__element,
:host(.ds-text--truncate-4) .ds-text__element,
:host(.ds-text--truncate-5) .ds-text__element {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
}

:host(.ds-text--truncate-2) .ds-text__element { -webkit-line-clamp: 2; }
:host(.ds-text--truncate-3) .ds-text__element { -webkit-line-clamp: 3; }
:host(.ds-text--truncate-4) .ds-text__element { -webkit-line-clamp: 4; }
:host(.ds-text--truncate-5) .ds-text__element { -webkit-line-clamp: 5; }

/* ── Wrap ───────────────────────────────────────────────────────────────── */

:host(.ds-text--nowrap),
:host(.ds-text--nowrap) .ds-text__element {
  white-space: nowrap;
  word-break: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

:host(.ds-text--balance),
:host(.ds-text--balance) .ds-text__element { text-wrap: balance; }

:host(.ds-text--pretty),
:host(.ds-text--pretty) .ds-text__element { text-wrap: pretty; }

/* ── Color ──────────────────────────────────────────────────────────────── */

:host(.ds-text--color-primary)   { color: var(--color-foreground-primary); }
:host(.ds-text--color-secondary) { color: var(--color-foreground-secondary); }
:host(.ds-text--color-tertiary)  { color: var(--color-foreground-tertiary); }
:host(.ds-text--color-quaternary) { color: var(--color-foreground-quaternary); }
:host(.ds-text--color-brand)     { color: var(--color-foreground-bold-brand); }
:host(.ds-text--color-negative)  { color: var(--color-foreground-bold-negative); }
:host(.ds-text--color-positive)  { color: var(--color-foreground-bold-positive); }
:host(.ds-text--color-warning)   { color: var(--color-foreground-bold-warning); }
:host(.ds-text--color-caution)   { color: var(--color-foreground-bold-caution); }
:host(.ds-text--color-ai)        { color: var(--color-foreground-bold-ai); }
:host(.ds-text--color-on-strong) { color: var(--color-foreground-on-strong-background-primary); }
:host(.ds-text--color-on-bold)   { color: var(--color-foreground-on-bold-background-primary); }
/*
 * The inherit state intentionally has no declaration. `color` inherits
 * naturally, while omitting a host rule lets an owning component apply a
 * semantic token directly to ds-text without depending on stylesheet order.
 */

/* ── Decorations ─────────────────────────────────────────────────────────── */

:host(.ds-text--decoration-underline),
:host(.ds-text--decoration-dotted-underline) {
  text-decoration-color: var(--ds-text-decoration-color, var(--color-foreground-tertiary));
  text-decoration-thickness: var(--ds-text-decoration-thickness, var(--dimension-stroke-width-012));
  text-underline-offset: var(--ds-text-decoration-offset, var(--dimension-space-025));
}

:host(.ds-text--decoration-underline) {
  text-decoration-line: underline;
}

:host(.ds-text--decoration-dotted-underline) {
  text-decoration-line: underline;
  text-decoration-style: dotted;
}

/* ── Style ──────────────────────────────────────────────────────────────── */

:host(.ds-text--italic) { font-style: italic; }

:host(.ds-text--font-feature-tabular-nums) {
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
}

/* ── Align ──────────────────────────────────────────────────────────────── */

:host(.ds-text--align-left)   { text-align: left; }
:host(.ds-text--align-center) { text-align: center; }
:host(.ds-text--align-right)  { text-align: right; }
